/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef, useContext } from 'react';
import { CalendarCell as CalendarCellCore, type CalendarCellProps as CalendarCellCoreProps, type CalendarCellRenderProps, CalendarStateContext, type ContextValue, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { View } from '../view/view.component';

interface CalendarCellProps extends Omit<CalendarCellCoreProps, 'style' | 'className'> {
  /** Whether the calendar cell should be small. @default false */
  isSmall?: boolean;
  style?: stylex.StyleXStyles;
}

const CalendarCellContext = createContext<ContextValue<CalendarCellProps, HTMLTableCellElement>>({});

const CalendarCell = forwardRef((props: CalendarCellProps, ref: ForwardedRef<HTMLTableCellElement>) => {
  // @ts-expect-error-next-line - no need to specify the default date
  [props, ref] = useContextProps(props, ref, CalendarCellContext);

  const { style, children, isSmall = false, ...coreProps } = props;

  const calendarState = useContext(CalendarStateContext);
  const isSingleCalendar = calendarState !== null;

  return (
    <CalendarCellCore
      ref={ref}
      {...applyStylesWithRenderProps<CalendarCellRenderProps>((state) => stylex.props(
        styles.base,
        state.isSelected && !isSingleCalendar && !(state.isSelectionStart || state.isSelectionEnd) && styles.selected,
        state.isSelectionStart && styles.selectedStart,
        state.isSelectionEnd && styles.selectedEnd,
        state.isSelectionStart && state.isSelectionEnd && styles.selectedStartAndEnd,
        state.isOutsideMonth && !state.isOutsideVisibleRange && styles.outsideMonth,
        style))}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        return (
          <View style={[
            selectionIndicatorStyles.base,
            isSmall && selectionIndicatorStyles.small,
            renderProps.isHovered && !renderProps.isSelected && selectionIndicatorStyles.hovered,
            (
              renderProps.isSelected &&
              (!(renderProps.isSelectionStart || renderProps.isSelectionEnd) || isSingleCalendar)
            ) && selectionIndicatorStyles.selectedInRange,
            (
              (renderProps.isSelectionStart || renderProps.isSelectionEnd) ||
              (renderProps.isSelected && isSingleCalendar)
            ) && selectionIndicatorStyles.selected,
            (
              ((renderProps.isSelectionStart || renderProps.isSelectionEnd) || (renderProps.isSelected && isSingleCalendar)) &&
              (renderProps.isHovered || renderProps.isFocusVisible) && !renderProps.isOutsideMonth &&
              selectionIndicatorStyles.selectedHovered
            ),
            (renderProps.isDisabled || renderProps.isUnavailable) && selectionIndicatorStyles.disabled,
            renderProps.isOutsideMonth && !renderProps.isOutsideVisibleRange && selectionIndicatorStyles.outsideMonth,
            renderProps.isOutsideVisibleRange && selectionIndicatorStyles.outsideVisibleRange,
            renderProps.isFocusVisible && selectionIndicatorStyles.focused,
          ]}>
            {evaluatedChildren ?? renderProps.defaultChildren}
            {renderProps.isToday &&
              <View style={[
                todayIndicatorStyles.base,
                isSmall && todayIndicatorStyles.small,
                renderProps.isSelected && !renderProps.isOutsideMonth && todayIndicatorStyles.selected,
                (
                  renderProps.isSelected &&
                  !renderProps.isOutsideMonth &&
                  !(renderProps.isSelectionStart || renderProps.isSelectionEnd)
                ) && todayIndicatorStyles.selectedInRange,
                renderProps.isHovered && !renderProps.isOutsideMonth && todayIndicatorStyles.hovered,
                (
                  (renderProps.isSelected && !renderProps.isOutsideMonth && (renderProps.isSelectionStart || renderProps.isSelectionEnd)) &&
                  (renderProps.isHovered || renderProps.isFocusVisible)
                ) && todayIndicatorStyles.selectedHovered,
                (renderProps.isDisabled || renderProps.isUnavailable) && todayIndicatorStyles.disabled,
              ]}
              />}
          </View>
        );
      }}
    </CalendarCellCore>
  );
});

const styles = stylex.create({
  base: {
    outline: 'none',
    backgroundColor: $.surfaceBGPrimary,
  },
  selected: {
    backgroundColor: $.surfaceHighlightSilent,
  },
  selectedStart: {
    backgroundImage: `linear-gradient(to right, transparent 50%, ${$.surfaceHighlightSilent} 50%)`,
  },
  selectedEnd: {
    backgroundImage: `linear-gradient(to left, transparent 50%, ${$.surfaceHighlightSilent} 50%)`,
  },
  selectedStartAndEnd: {
    backgroundImage: null,
  },
  outsideMonth: {
    backgroundColor: $.surfaceBGPrimary,
    backgroundImage: null,
  },
});

const selectionIndicatorStyles = stylex.create({
  base: {
    margin: 9,
    borderColor: 'transparent',
    borderRadius: 999,
    borderStyle: 'solid',
    borderWidth: 2,
    alignItems: 'center',
    color: $.textPrimary,
    cursor: 'pointer',
    display: 'flex',
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 400,
    forcedColorAdjust: 'none',
    justifyContent: 'center',
    letterSpacing: -0.45,
    lineHeight: '18px',
    position: 'relative',
    textAlign: 'center',
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
    height: 38,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 38,
  },
  small: {
    marginBlock: 9,
    marginInline: 6.5,
    height: 30,
    width: 30,
  },
  selected: {
    backgroundColor: $.surfaceHighlight,
    color: $.textInverted,
  },
  selectedInRange: {
    color: $.textInteractive,
  },
  hovered: {
    backgroundColor: $.surfaceHighlightMedium,
    color: $.textInteractive,
  },
  selectedHovered: {
    borderColor: $.borderInteractive,
    backgroundColor: $.surfaceBGPrimary,
    color: $.textPrimary,
  },
  disabled: {
    backgroundColor: $.surfaceBGPrimary,
    color: $.textDisabled,
    cursor: 'not-allowed',
  },
  outsideVisibleRange: {
    color: $.textDisabled,
  },
  outsideMonth: {
    backgroundColor: $.surfaceBGPrimary,
    color: $.textDisabled,
  },
  focused: {
    outlineColor: $.borderInteractive,
    outlineOffset: '4px',
    outlineStyle: 'solid',
    outlineWidth: '2px',
  }
});

const todayIndicatorStyles = stylex.create({
  base: {
    borderRadius: 999,
    backgroundColor: {
      default: $.iconQuaternary,
      ':is([data-hovered])': $.iconPrimary,
    },
    content: '',
    display: 'block',
    position: 'absolute',
    transform: 'translateX(-50%)',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color',
    transitionTimingFunction: 'easy-in-out',
    bottom: 5.5,
    height: 3,
    left: '50%',
    width: 3,
  },
  small: {
    bottom: 2.5,
  },
  selected: {
    backgroundColor: $.textInverted,
  },
  selectedInRange: {
    backgroundColor: $.textInteractive
  },
  hovered: {
    backgroundColor: $.textInteractive,
  },
  selectedHovered: {
    backgroundColor: $.textPrimary,
  },
  disabled: {
    backgroundColor: $.textDisabled,
  },
});

export { CalendarCell, CalendarCellContext, type CalendarCellProps };
